import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionLayerListBloc } from '@connect-services/client/blocs/field-intervention-layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { AbstractPaginationComponent } from '@connect-services/client/components/abstract-pagination/abstract-pagination.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { PaginationFooterTemplateComponent } from '@connect-services/client/components/pagination-footer-template/pagination-footer-template.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { FieldInterventionsService, LayerWithAttributes } from '@connect-services/client/sdk/api';
import { defaultFieldInterventionLayerListPaginationParameters } from '@connect-services/client/services/entities-table-by-layer.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { labelByLayerType } from '@connect-services/client/services/layer.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

const NUMBER_OF_ENTITIES = 'numberOfEntities';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        PaginationFooterTemplateComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        TitleComponent,
        TranslocoPipe,
    ],
    selector: 'app-field-intervention-layers-filtered-table',
    templateUrl: './field-intervention-layers-filtered-table.component.html',
})
export class FieldInterventionLayersFilteredTableComponent extends AbstractPaginationComponent {
    public readonly isLoading: WritableSignal<boolean> = signal(true);
    public readonly layers: WritableSignal<LayerWithAttributes[]> = signal([]);
    public readonly paginator: WritableSignal<PaginatorComponent | undefined> = signal(undefined);

    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionLayerListBloc = inject(FieldInterventionLayerListBloc);
    protected readonly fieldInterventionService = inject(FieldInterventionService);
    protected readonly fieldInterventionServiceSdk = inject(FieldInterventionsService);
    protected readonly labelByLayerType = labelByLayerType;
    protected readonly NUMBER_OF_ENTITIES = NUMBER_OF_ENTITIES;
    protected Object = Object;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly tableColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'layerName',
        },
        {
            key: 'depositFolder.name',
            label: 'dataName',
        },
        {
            key: 'type',
            label: 'Type',
        },
        {
            key: NUMBER_OF_ENTITIES,
            label: NUMBER_OF_ENTITIES,
            sortable: false,
        },
    ];

    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);

    public constructor() {
        super();

        this.paginationParameters = {
            ...defaultFieldInterventionLayerListPaginationParameters,
        };

        effect(() => {
            untracked(() => {
                if (!this.fieldInterventionBloc.value() || !this.fieldInterventionLayerListBloc.value()) {
                    return;
                }

                this.layers.set(this.fieldInterventionLayerListBloc.value()!.items);

                this.paginationParameters = {
                    ...this.paginationParameters,
                    page: this.fieldInterventionLayerListBloc.value()!.page,
                    take: this.fieldInterventionLayerListBloc.value()!.take,
                    totalItems: this.fieldInterventionLayerListBloc.value()!.totalItems,
                };

                this.isLoading.set(false);
            });
        });
    }

    public async goToPreviousPage(): Promise<void> {
        const path = [
            RoutePathEnum.PROJECTS,
            this.projectBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTIONS,
            this.fieldInterventionBloc.value()?.id,
            RoutePathEnum.FIELD_INTERVENTION_DASHBOARD,
        ];
        await this.router.navigate(path);
    }

    public async onPageChange(page: number): Promise<void> {
        this.paginationParameters.page = page;

        await this.fieldInterventionLayerListBloc.load({
            id: this.fieldInterventionBloc.value()!.id,
            ...this.paginationParameters,
            page: page,
        });

        this.layers.set(this.fieldInterventionLayerListBloc.value()!.items);

        this.paginationParameters = {
            ...this.paginationParameters,
            page: this.fieldInterventionLayerListBloc.value()!.page,
        };
    }

    public async onSortChange(sort?: SortState): Promise<void> {
        if (!this.paginator()) {
            return;
        }
        this.paginator()!.resetPaginator();

        const layersSorted = await firstValueFrom(
            this.fieldInterventionServiceSdk.fieldInterventionControllerGetLayersWithAttributes({
                id: this.fieldInterventionBloc.value()!.id,
                ...this.paginationParameters,
                ...sort,
            }),
        );

        this.layers.set(layersSorted.items);
    }
}
